import type { QueryView } from '@krp/client-runtime'

import type { MutationStateVm, ShareVm, StateBadgeVm, TestingMean, TestingMetricDelta, TestingProportion, TestingRunState } from './types'

/**
 * Модель экрана «Похожие варианты» (RP-FB67): семейства двойников из каталога, кадры вариантов и замер «не перепутал ли поиск вариант».
 * Данные — ответы сервера (`Platform.Contracts/Testing/TwinTestingContracts.cs`) как есть; vm добавляет к ним слова и проценты.
 */

export const TWIN_PRIORITIES = ['partial', 'full', 'none'] as const
export type TwinPriority = (typeof TWIN_PRIORITIES)[number]

export const isTwinPriority = (value: string): value is TwinPriority => TWIN_PRIORITIES.some((priority) => priority === value)

/** Состав замера: кадры тех, у кого в серии есть двойник, либо все кадры (нужно выгрузке для калибровки). */
export type TwinScope = 'twins' | 'all-frames'
export const TWIN_SCOPES: readonly TwinScope[] = ['twins', 'all-frames']

export interface TwinVariant {
  catalogProductId: string
  name: string
  hasPhoto: boolean
  differences: string
  testProductId: string | null
  frames: number
}

export interface TwinFamily {
  key: string
  series: string
  priority: TwinPriority
  withPhoto: number
  variants: readonly TwinVariant[]
}

export interface TwinFamilies {
  families: readonly TwinFamily[]
  total: number
  catalogProducts: number
  builtAt: string
}

/** Что ответил сервер на «Добавить кадр двойника»: тестовый товар варианта и сколько у него теперь кадров. */
export interface TwinFrameAdded {
  testProductId: string
  frames: number
  references: number
}

export interface TwinSeriesSlice {
  series: string
  frames: number
  confused: TestingProportion
  choiceOffered: TestingProportion
  foundCorrect: TestingProportion
}

export interface TwinConfusion {
  expectedName: string
  insteadName: string
  count: number
}

export interface TwinRunMetrics {
  confused: TestingProportion
  topIsTwin: TestingProportion
  choiceOffered: TestingProportion
  foundVerdict: TestingProportion
  foundCorrect: TestingProportion
  photoFixedText: TestingProportion
  photoSpoiledText: TestingProportion
  mrr: TestingMean
  series: readonly TwinSeriesSlice[]
  confusions: readonly TwinConfusion[]
  latency: { n: number; medianMs: number | null; p95Ms: number | null }
  twinFrames: number
  otherFrames: number
  errors: number
}

export interface TwinRunStamp {
  verdict: string
  confidenceFormula: string
  photoPrep: string
  platformVersion: string
  encoderModel: string | null
  scope: string
  frames: number
  families: number
  variants: number
  twinPhotoCoverage: number | null
}

export interface TwinRun {
  id: string
  state: TestingRunState
  scope: string
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  totalFrames: number
  doneFrames: number
  error: string | null
  createdBy: string
  stamp: TwinRunStamp | null
  metrics: TwinRunMetrics | null
}

export interface TwinRunDetail {
  run: TwinRun
  comparison: { previousRunId: string; sameStamp: boolean; deltas: readonly TestingMetricDelta[] } | null
}

// --- vm -----------------------------------------------------------------------------------------------------------------

export interface TwinVariantVm {
  catalogProductId: string
  name: string
  differences: string
  photo: StateBadgeVm
  framesText: string
  /** Серия, которую получает кадр этого варианта. */
  series: string
}

export interface TwinFamilyVm {
  key: string
  series: string
  priority: StateBadgeVm
  summary: string
  variants: readonly TwinVariantVm[]
  /** Сколько вариантов ещё без кадра: по ним помощник «снять серию» ведёт по очереди. */
  missingFrames: number
}

export interface TwinFamiliesVm {
  query: QueryView
  families: readonly TwinFamilyVm[]
  /** Показано из найденного, когда найденное не поместилось. */
  note: string | null
  builtAtText: string | null
  /** Какой приоритет выбран для отбора (`null` — все). */
  priority: TwinPriority | null
  action: MutationStateVm
}

export interface TwinRunRowVm {
  id: string
  state: StateBadgeVm
  when: string
  scopeText: string
  progress: string
  confused: ShareVm | null
  cancellable: boolean
}

export interface TwinRunsVm {
  query: QueryView
  rows: readonly TwinRunRowVm[]
  /** Разбор выбранного замера (по умолчанию — последнего готового); `null` — разбирать нечего. */
  detail: TwinRunDetailVm | null
  /** Почему новый замер сейчас не запустить (очередь замеров общая); `null` — можно. */
  running: string | null
  /** Почему выгрузка для калибровки сейчас недоступна; `null` — можно. */
  exportBlocked: string | null
  action: MutationStateVm
}

export interface TwinRunDetailVm {
  id: string
  title: string
  state: StateBadgeVm
  progress: string
  error: string | null
  scores: readonly { key: string; label: string; share: ShareVm; hint: string }[]
  series: readonly { series: string; frames: number; confused: ShareVm; choiceOffered: ShareVm; foundCorrect: ShareVm }[]
  confusions: readonly { key: string; text: string; count: number }[]
  frames: string
  latency: string | null
  stamp: readonly { label: string; value: string }[]
  comparison: {
    previousRunId: string
    note: string | null
    rows: readonly { label: string; previous: string; current: string; delta: string }[]
  } | null
}

// --- действия -----------------------------------------------------------------------------------------------------------

export interface TwinFrameDraft {
  /** Вариант из каталога, которому снят кадр. */
  catalogProductId: string
  series: string
  file: File
}

export interface TwinActions {
  reload: () => void
  /** Пересчитать семейства по каталогу, не дожидаясь, пока устареет прежний расчёт. */
  recompute: () => void
  /** Отобрать семейства по приоритету; `null` — все. */
  filter: (priority: TwinPriority | null) => void
  /** Добавить кадр варианта. Решение принимает сервер; `true` — кадр принят и лист можно закрывать. */
  addFrame: (draft: TwinFrameDraft) => Promise<boolean>
  startRun: (scope: TwinScope) => void
  cancelRun: (runId: string) => void
  /** Показать разбор замера. */
  selectRun: (runId: string) => void
  download: (format: 'json' | 'csv') => void
  openProducts: () => void
}
